/**
 * Billing.
 *
 * Everything that touches money happens in Stripe's hosted flows — Checkout to
 * subscribe, the Customer Portal to change a card, cancel or download an invoice.
 * This page's job is to state the current position accurately and hand off. That is
 * a deliberate scope choice: card data never reaches our origin, so our PCI surface
 * stays at zero.
 */

import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { planPricing } from '@/entities/pricing'
import type { BillingInterval, SubscriptionView } from '@/entities/types'
import {
  useAccount,
  useBillingPortal,
  useCatalog,
  useCheckout,
  useSetSeatQuantity,
  useSubmitEnterpriseLead,
  useSubscription,
} from '@/shared/api/hooks'
import { AppShell, PageHead } from '@/shared/ui/Chrome'
import {
  Badge,
  Button,
  ErrorNotice,
  Field,
  Fig,
  Loading,
  Modal,
  Notice,
  Panel,
  Readout,
  StatusBadge,
  formatDate,
  formatPrice,
} from '@/shared/ui/primitives'
import { Turnstile } from '@/shared/ui/Turnstile'

export function Billing() {
  const [params] = useSearchParams()
  const { data: account } = useAccount()
  const { data: catalog } = useCatalog()

  const organization = account?.organizations.find((org) => org.has_seat) ?? account?.organizations[0]
  const orgId = organization?.id
  const canManageBilling = !organization || organization.role === 'owner' || organization.role === 'billing_admin'

  const { data: subscription, isPending, error } = useSubscription(orgId)
  const checkout = useCheckout()
  const portal = useBillingPortal()
  const enterpriseLead = useSubmitEnterpriseLead()

  // Enterprise Inbound Form State
  const [enterpriseOpen, setEnterpriseOpen] = useState(false)
  const [enterpriseSubmitted, setEnterpriseSubmitted] = useState(false)
  const [inquiryName, setInquiryName] = useState('')
  const [inquiryEmail, setInquiryEmail] = useState('')
  const [inquiryCompany, setInquiryCompany] = useState('')
  const [inquiryTeamSize, setInquiryTeamSize] = useState('20-50')
  const [inquiryNotes, setInquiryNotes] = useState('')
  const [cfToken, setCfToken] = useState('')
  const [honeypot, setHoneypot] = useState('')

  function openEnterpriseModal() {
    if (account) {
      if (!inquiryName) setInquiryName(account.display_name || '')
      if (!inquiryEmail) setInquiryEmail(account.email || '')
      if (!inquiryCompany && organization) setInquiryCompany(organization.name || '')
    }
    setEnterpriseSubmitted(false)
    setEnterpriseOpen(true)
  }

  function submitEnterprise(e: React.FormEvent) {
    e.preventDefault()
    if (honeypot) return
    enterpriseLead.mutate(
      {
        name: inquiryName.trim(),
        email: inquiryEmail.trim(),
        company: inquiryCompany.trim(),
        teamSize: inquiryTeamSize,
        notes: inquiryNotes.trim(),
        cfToken,
      },
      {
        onSuccess: () => {
          setEnterpriseSubmitted(true)
        },
      },
    )
  }

  // Annual by default here too, so the panel quotes the same thing the public pricing
  // page did on the way in.
  const [interval, setInterval] = useState<BillingInterval>('annual')
  const returned = params.get('status')

  const discount = catalog?.annual_discount_percent
  const teamPlan = catalog?.plans?.find((p) => p.code === 'team')
  const annualDiscount =
    discount ?? (teamPlan ? planPricing(teamPlan, 'annual')?.discountPercent ?? 20 : 20)
  const teamPricing = teamPlan ? planPricing(teamPlan, interval, discount) : null
  const teamMinSeats = teamPlan?.min_paid_seats ?? 3
  const teamMaxSeats = teamPlan?.max_paid_seats ?? 200

  // Read from account and catalog, defaulting to true so the plan selector is visible
  // unless explicitly disabled by the server deployment.
  const purchasesOpen = account?.purchases_enabled ?? catalog?.purchases_enabled ?? true

  return (
    <AppShell>
      <PageHead
        index="02"
        eyebrow="Billing"
        title="Subscription"
        subtitle="Plans, seats, invoices and payment methods."
        actions={
          canManageBilling && subscription?.manage_in_portal ? (
            <Button
              size="sm"
              loading={portal.isPending}
              onClick={() => portal.mutate(orgId)}
            >
              Open billing portal
            </Button>
          ) : undefined
        }
      />

      <div className="stack" style={{ ['--gap' as string]: 'var(--s5)' }}>
        {/*
          The Checkout return URL is attacker-supplied input, so a "success" in it
          means only "the browser came back" — never "access granted". Access changes
          when the verified webhook is applied, which is what this wording reflects.
        */}
        {returned === 'success' && (
          <Notice tone="ok">
            Payment received. Your subscription activates as soon as we have confirmation
            from Stripe — usually a few seconds. Reload if this page still looks stale.
          </Notice>
        )}
        {returned === 'canceled' && (
          <Notice tone="warn">Checkout was cancelled. Nothing was charged.</Notice>
        )}

        {isPending && <Loading label="Loading your subscription" />}
        <ErrorNotice error={error} />
        <ErrorNotice error={checkout.error} />
        <ErrorNotice error={portal.error} />

        {subscription?.found ? (
          <>
            <Panel title="Current subscription" index="01">
              <div className="readout-grid" style={{ border: 'none', background: 'transparent' }}>
                <div style={{ padding: 0, background: 'transparent' }}>
                  <Readout
                    value={(subscription.plan_code ?? '—').toUpperCase()}
                    caption={subscription.interval ?? 'plan'}
                    tone="signal"
                  />
                </div>
                <div style={{ padding: 0, background: 'transparent' }}>
                  <div className="readout">
                    <div style={{ paddingBlock: '0.35rem' }}>
                      <StatusBadge status={subscription.status} />
                    </div>
                    <div className="readout__caption">status</div>
                  </div>
                </div>
                {subscription.seat_quantity ? (
                  <div style={{ padding: 0, background: 'transparent' }}>
                    <Readout
                      value={`${subscription.seats_assigned ?? 0}/${subscription.seat_quantity}`}
                      caption="seats in use"
                    />
                  </div>
                ) : null}
                <div style={{ padding: 0, background: 'transparent' }}>
                  <div className="readout">
                    <div className="readout__value" style={{ fontSize: '1.1rem', paddingBlock: '0.5rem' }}>
                      {formatDate(subscription.current_period_end)}
                    </div>
                    <div className="readout__caption">
                      {subscription.cancel_at_period_end ? 'access ends' : 'renews'}
                    </div>
                  </div>
                </div>
              </div>

              {subscription.active_billing && subscription.seat_quantity ? (
                <div style={{ marginTop: 'var(--s4)' }}>
                  <ActiveBillingNotice
                    billing={subscription.active_billing}
                    capacity={subscription.seat_quantity}
                  />
                </div>
              ) : null}

              {subscription.cancel_at_period_end && (
                <div style={{ marginTop: 'var(--s4)' }}>
                  <Notice tone="warn">
                    This subscription is set to cancel on{' '}
                    {formatDate(subscription.current_period_end)}. You keep full access until
                    then. Reactivate from the billing portal.
                  </Notice>
                </div>
              )}
            </Panel>

            {/* Seat quantity, Team only. */}
            {canManageBilling && orgId && subscription.seat_quantity ? (
              <SeatQuantityPanel
                orgId={orgId}
                purchased={subscription.seat_quantity}
                assigned={subscription.seats_assigned ?? 0}
              />
            ) : null}

            {canManageBilling ? (
              <Panel title="Invoices and payment method" index="03">
                <div className="stack" style={{ ['--gap' as string]: 'var(--s4)' }}>
                  <p className="dim" style={{ margin: 0 }}>
                    Invoices, receipts, card details, billing address and cancellation are all
                    handled by Stripe's customer portal. Card data never touches our servers.
                  </p>
                  <div>
                    <Button
                      variant="primary"
                      loading={portal.isPending}
                      onClick={() => portal.mutate(orgId)}
                    >
                      Open billing portal
                    </Button>
                  </div>
                </div>
              </Panel>
            ) : (
              <Panel title="Billing administration" index="02">
                <div className="stack" style={{ ['--gap' as string]: 'var(--s4)' }}>
                  <p style={{ margin: 0 }}>
                    Your seat is provided and billed through <strong>{organization?.name ?? 'your organization'}</strong>.
                  </p>
                  <p className="dim" style={{ margin: 0 }}>
                    Subscription changes, seat quantities, and invoice history are managed by your workspace Owner or Billing Admin.
                  </p>
                </div>
              </Panel>
            )}
          </>
        ) : !isPending && !canManageBilling ? (
          <Panel title="Workspace billing" index="01">
            <div className="stack" style={{ ['--gap' as string]: 'var(--s4)' }}>
              <p style={{ margin: 0 }}>
                You are a member of <strong>{organization?.name ?? 'your organization'}</strong> on the 2-Seat Free Team evaluation.
              </p>
              <p className="dim" style={{ margin: 0 }}>
                Upgrading to Archis Team ({teamMinSeats}+ seats) or modifying workspace subscriptions must be performed by the workspace Owner or Billing Admin.
              </p>
            </div>
          </Panel>
        ) : !isPending && !purchasesOpen ? (
          /* Nothing to sell yet. This panel replaces the plan picker entirely rather
             than disabling its buttons: a form you cannot submit invites somebody to
             fill it in and then tells them no. */
          <Panel title="Not on sale yet" index="01">
            <div className="stack" style={{ ['--gap' as string]: 'var(--s4)' }}>
              <p className="dim" style={{ margin: 0 }}>
                Purchasing is not open on this deployment, so there is nothing to
                subscribe to for now. When it opens you will be able to do it from this
                page, and we will say so before anything changes. Archis Free keeps working
                either way.
              </p>

              <p className="mono-note" style={{ margin: 0 }}>
                What Archis will cost is published on the{' '}
                <Link to="/pricing" className="signal">
                  pricing page
                </Link>
                .
              </p>
            </div>
          </Panel>
        ) : (
          !isPending && (
            <Panel title="Choose a plan" index="01">
              <div className="stack" style={{ ['--gap' as string]: 'var(--s5)' }}>
                <div className="row" style={{ gap: 'var(--s2)' }}>
                  <span className="mono-note">Billing</span>
                  <div
                    className="row"
                    style={{ gap: 0, border: '1px solid var(--rule-bright)', borderRadius: 'var(--radius)' }}
                  >
                    {(['monthly', 'annual'] as const).map((option) => (
                      <button
                        key={option}
                        className="btn"
                        onClick={() => setInterval(option)}
                        aria-pressed={interval === option}
                        style={{
                          border: 'none',
                          borderRadius: 0,
                          background: interval === option ? 'var(--signal-wash)' : 'transparent',
                          color: interval === option ? 'var(--signal)' : 'var(--text-dim)',
                        }}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                  {interval === 'annual' && annualDiscount > 0 && (
                    <Badge tone="signal">save {annualDiscount}%</Badge>
                  )}
                </div>

                <div className="grid-2">
                  {/* Archis Team Card */}
                  <div className="card">
                    <div className="row row--between">
                      <strong>Archis Team</strong>
                      <Badge tone="signal">
                        {teamMinSeats}–{teamMaxSeats} seats
                      </Badge>
                    </div>
                    {/* Per month on both intervals, matching the public pricing page. */}
                    <div className="price" style={{ fontSize: '2rem', marginTop: 'var(--s3)' }}>
                      {teamPricing ? formatPrice(teamPricing.perMonthCents) : (interval === 'annual' ? '$8' : '$10')}
                      <span className="price__per"> / seat / mo</span>
                    </div>
                    <p className="mono-note" style={{ marginTop: 'var(--s2)' }}>
                      {teamPricing && interval === 'annual'
                        ? `${formatPrice(teamPricing.chargedCents)} per seat billed once a year`
                        : 'Billed monthly'}
                      {' · '}
                      minimum {teamMinSeats} seats
                    </p>
                    <div style={{ marginTop: 'var(--s4)' }}>
                      <Button
                        block
                        variant="primary"
                        loading={checkout.isPending}
                        onClick={() =>
                          checkout.mutate({
                            plan: 'team',
                            interval,
                            seats: teamMinSeats,
                            ...(orgId ? { organizationId: orgId } : {}),
                          })
                        }
                      >
                        Subscribe to Team
                      </Button>
                    </div>
                  </div>

                  {/* Archis Enterprise Card */}
                  <div className="card">
                    <div className="row row--between">
                      <strong>Archis Enterprise</strong>
                      <Badge tone="signal">Any size</Badge>
                    </div>
                    <div className="price" style={{ fontSize: '2rem', marginTop: 'var(--s3)' }}>
                      Custom
                      <span className="price__per"> / quoted annually</span>
                    </div>
                    <p className="mono-note" style={{ marginTop: 'var(--s2)' }}>
                      SAML SSO · SCIM · SIEM audit streaming · offline licences
                    </p>
                    <div style={{ marginTop: 'var(--s4)' }}>
                      <Button
                        block
                        variant="default"
                        onClick={openEnterpriseModal}
                      >
                        Contact Enterprise Sales
                      </Button>
                    </div>
                  </div>
                </div>

                <p className="mono-note" style={{ margin: 0 }}>
                  Full comparison on the{' '}
                  <Link to="/pricing#matrix" className="signal">
                    pricing page
                  </Link>
                  . Payment is processed by Stripe.
                </p>
              </div>
            </Panel>
          )
        )}
      </div>

      {enterpriseOpen && (
        <Modal
          title={enterpriseSubmitted ? 'Inquiry Received' : 'Contact Enterprise Sales'}
          onClose={() => {
            setEnterpriseOpen(false)
            setEnterpriseSubmitted(false)
          }}
        >
          {enterpriseSubmitted ? (
            <div className="stack" style={{ ['--gap' as string]: 'var(--s4)' }}>
              <Notice tone="ok">
                Thank you for reaching out! We have received your enterprise request.
              </Notice>
              <p className="dim" style={{ margin: 0, fontSize: '0.875rem' }}>
                Our solutions engineering team has received your inquiry for <strong>{inquiryCompany || 'your team'}</strong> and
                will follow up at <strong>{inquiryEmail}</strong> within 1 business day to arrange a tailored demo and security review.
              </p>
              <Button variant="primary" onClick={() => setEnterpriseOpen(false)}>
                Close
              </Button>
            </div>
          ) : (
            <form onSubmit={submitEnterprise} className="stack" style={{ ['--gap' as string]: 'var(--s4)' }}>
              <p className="dim" style={{ margin: 0, fontSize: '0.875rem' }}>
                Enterprise licenses include Okta/SAML SSO, SCIM provisioning, SIEM streaming, offline licences and our DPA on request (in counsel review), at any team size:
              </p>

              <Field label="Your Name">
                <input
                  className="input"
                  type="text"
                  required
                  placeholder="Ada Lovelace"
                  value={inquiryName}
                  onChange={(e) => setInquiryName(e.target.value)}
                />
              </Field>

              <Field label="Work Email">
                <input
                  className="input"
                  type="email"
                  required
                  placeholder="ada@company.com"
                  value={inquiryEmail}
                  onChange={(e) => setInquiryEmail(e.target.value)}
                />
              </Field>

              <div className="grid-2" style={{ gap: 'var(--s3)' }}>
                <Field label="Company / Workspace Name">
                  <input
                    className="input"
                    type="text"
                    required
                    placeholder="Acme Corp"
                    value={inquiryCompany}
                    onChange={(e) => setInquiryCompany(e.target.value)}
                  />
                </Field>

                <Field label="Engineering Team Size">
                  <select
                    className="input"
                    value={inquiryTeamSize}
                    onChange={(e) => setInquiryTeamSize(e.target.value)}
                  >
                    <option value="20-50">20–50 developers</option>
                    <option value="50-200">50–200 developers</option>
                    <option value="200-1000">200–1,000 developers</option>
                    <option value="1000+">1,000+ developers</option>
                  </select>
                </Field>
              </div>

              <Field label="Specific Requirements (Optional)">
                <textarea
                  className="input"
                  rows={3}
                  placeholder="SAML / Okta SSO, security questionnaire, number of repositories..."
                  value={inquiryNotes}
                  onChange={(e) => setInquiryNotes(e.target.value)}
                />
              </Field>

              {/* Anti-bot Honeypot field (invisible to humans) */}
              <div style={{ display: 'none', position: 'absolute', left: '-9999px' }} aria-hidden="true">
                <label htmlFor="billing-modal-website">Website</label>
                <input
                  id="billing-modal-website"
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                />
              </div>

              {/* Cloudflare Turnstile Bot Protection */}
              <Turnstile onVerify={setCfToken} onExpire={() => setCfToken('')} />

              <ErrorNotice error={enterpriseLead.error} />

              <div className="row row--between" style={{ marginTop: 'var(--s2)' }}>
                <Button type="button" variant="ghost" onClick={() => setEnterpriseOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" loading={enterpriseLead.isPending}>
                  Submit Request →
                </Button>
              </div>
            </form>
          )}
        </Modal>
      )}
    </AppShell>
  )
}

/**
 * Seat quantity control.
 *
 * The lower bound is the number of seats currently occupied, enforced in the input
 * as well as on the server (blueprint §7.5: reducing below assigned seats requires
 * removing members first). Showing why the floor exists prevents the support ticket.
 */
/**
 * This period's active developers (#930). A developer is active when they ran
 * Archis on this workspace's licence at least once in the billing period; CI
 * tokens do not count.
 */
function ActiveBillingNotice({
  billing,
  capacity,
}: {
  billing: NonNullable<SubscriptionView['active_billing']>
  capacity: number
}) {
  const active = `${billing.active_seats} of ${capacity} ${capacity === 1 ? 'seat' : 'seats'} active this period`
  const next = `${billing.next_billed_seats} ${billing.next_billed_seats === 1 ? 'seat' : 'seats'}`
  if (billing.mode === 'shadow') {
    return (
      <Notice>
        {active}. Billed by active developers, the next invoice would be for {next}. You are
        still billed for the {capacity} seats you bought.
      </Notice>
    )
  }
  return (
    <Notice>
      {active}. The next invoice is for the developers who used Archis this period, never
      fewer than the plan's minimum: {next} so far.
    </Notice>
  )
}

function SeatQuantityPanel({
  orgId,
  purchased,
  assigned,
}: {
  orgId: string
  purchased: number
  assigned: number
}) {
  const { data: catalog } = useCatalog()
  const teamPlan = catalog?.plans.find((p) => p.code === 'team')
  const minPaidSeats = teamPlan?.min_paid_seats ?? 3
  const maxPaidSeats = teamPlan?.max_paid_seats ?? 200
  const minAllowed = Math.max(assigned, minPaidSeats)

  const [quantity, setQuantity] = useState(purchased)
  const [lastAction, setLastAction] = useState<'increased' | 'decreased' | null>(null)
  const setSeats = useSetSeatQuantity(orgId)
  const dirty = quantity !== purchased
  const outOfRange = quantity < minAllowed || quantity > maxPaidSeats

  const handleUpdate = () => {
    if (outOfRange) return
    if (quantity > purchased) {
      setLastAction('increased')
    } else if (quantity < purchased) {
      setLastAction('decreased')
    }
    setSeats.mutate(quantity)
  }

  return (
    <Panel title="Seats" index="02">
      <div className="stack" style={{ ['--gap' as string]: 'var(--s4)' }}>
        <div className="row row--between">
          <Readout value={`${assigned}/${purchased}`} caption="assigned / purchased" />
          {assigned === purchased && <Badge tone="amber">all seats in use</Badge>}
        </div>

        <Field
          label="Purchased seats"
          hint={
            assigned > 0
              ? `Cannot go below ${assigned} while ${assigned === 1 ? 'seat is' : 'seats are'} in use. Archis Team supports ${minPaidSeats}–${maxPaidSeats} seats.`
              : `Archis Team supports ${minPaidSeats}–${maxPaidSeats} seats.`
          }
        >
          <input
            className="input input--mono"
            type="number"
            min={minAllowed}
            max={maxPaidSeats}
            value={quantity}
            onChange={(event) => setQuantity(Number(event.target.value))}
            style={{ maxWidth: 160 }}
          />
        </Field>

        {quantity > maxPaidSeats && (
          <Notice tone="warn">
            Archis Team supports up to {maxPaidSeats} self-serve seats. For more, or for SSO and SCIM, contact us about Enterprise.
          </Notice>
        )}

        <ErrorNotice error={setSeats.error} />
        {setSeats.isSuccess && !dirty && (
          <Notice tone="ok">
            {lastAction === 'increased'
              ? 'Seat count updated. Your card on file was charged for the prorated difference, and a receipt was sent to your billing email.'
              : lastAction === 'decreased'
              ? 'Seat count updated. Unused time has been credited toward your next renewal.'
              : 'Seat count updated.'}
          </Notice>
        )}

        <div className="row">
          <Button
            variant="primary"
            disabled={!dirty || outOfRange}
            loading={setSeats.isPending}
            onClick={handleUpdate}
          >
            Update to <Fig>{quantity}</Fig> seats
          </Button>
          {dirty && (
            <Button variant="ghost" onClick={() => setQuantity(purchased)}>
              Reset
            </Button>
          )}
        </div>

        <p className="mono-note" style={{ margin: 0 }}>
          Adding seats mid-cycle is prorated and charged immediately to your payment method on file. Removing seats takes effect immediately, and unused time is credited toward your next renewal.
        </p>
      </div>
    </Panel>
  )
}
